import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { Ionicons } from "@expo/vector-icons";
import { useState, useCallback, useEffect } from "react";
import { Link, useRouter, useFocusEffect } from "expo-router";
import { useUserChallenges, CHALLENGE_ICONS, CHALLENGE_COLORS } from "@/modules/challenges";
import { useNotifications } from "@/lib/notifications/useNotifications";
import { useTeamScores } from "@/modules/scores";
import { useAgendaEvents } from "@/modules/agenda";
import { useActiveSpeurtocht, ActiveSpeurttochtCard } from "@/modules/speurtocht";
import { ActiveQuizCard } from "@/modules/quiz-live";
import { WeekendBanner } from "@/components/WeekendBanner";
import { NoWeekendBanner } from "@/components/NoWeekendBanner";
import { UserSwitcher } from "@/components/UserSwitcher";
import { PlayingAsBanner } from "@/components/PlayingAsBanner";
import { supabase } from "@/lib/supabase/client";
import { format, isToday, isTomorrow, isThisWeek } from "date-fns";
import { nl } from "date-fns/locale";
import LottieView from "lottie-react-native";

/**
 * Waiting game interface
 */
interface WaitingGame {
  id: string;
  game_slug: string;
  other_player_name: string;
  created_at: string;
  mode: "free" | "weekend";
  isSentByMe: boolean;
}

/**
 * Home Screen - Main dashboard
 */
export default function HomeScreen() {
  const { profile, activeProfile, userTeam, roles, refreshProfile, activeWeekend, isSwitched, dependents, user } = useAuth();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const router = useRouter();
  const { activeChallenges, badgeCount } = useUserChallenges();
  const { unreadCount, refetch: refetchNotifications } = useNotifications();
  const { teams, getUserTeamPosition, getNextGoal, refresh: refreshScores } = useTeamScores();
  const { events, isLoading: isLoadingEvents, refetch: refetchEvents } = useAgendaEvents();
  const { activeSession, refresh: refreshActiveSpeurtocht } = useActiveSpeurtocht();
  const [refreshing, setRefreshing] = useState(false);
  const [waitingGames, setWaitingGames] = useState<WaitingGame[]>([]);
  const [activeGames, setActiveGames] = useState<WaitingGame[]>([]);

  // Use activeProfile for waiting games (supports guardian mode)
  const targetUserId = activeProfile?.id;

  /**
   * Fetch waiting games - includes both sent and received challenges
   * Uses activeProfile to support guardian mode
   */
  const fetchWaitingGames = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data, error } = await supabase
        .from("game_sessions")
        .select("id, game_slug, created_at, mode, game_state, created_by")
        .eq("status", "waiting")
        .eq("session_type", "versus")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const games: WaitingGame[] = (data || [])
        .filter((session) => {
          const gameState = session.game_state as Record<string, unknown> | null;
          const isChallengedUser = gameState?.challenged_user_id === targetUserId;
          const isChallenger = session.created_by === targetUserId;
          return isChallengedUser || isChallenger;
        })
        .map((session) => {
          const gameState = session.game_state as Record<string, unknown>;
          const isChallenger = session.created_by === targetUserId;
          
          return {
            id: session.id,
            game_slug: session.game_slug,
            other_player_name: isChallenger
              ? (gameState?.challenged_user_name as string) || "Tegenstander"
              : (gameState?.challenger_name as string) || "Iemand",
            created_at: session.created_at,
            mode: session.mode as "free" | "weekend",
            isSentByMe: isChallenger,
          };
        });

      setWaitingGames(games);
    } catch (err) {
      console.error("Error fetching waiting games:", err);
    }
  }, [targetUserId]);

  /**
   * Fetch active games - games in progress
   * Uses activeProfile to support guardian mode
   */
  const fetchActiveGames = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const { data, error } = await supabase
        .from("game_sessions")
        .select("id, game_slug, created_at, mode, game_state, created_by, participants")
        .eq("status", "active")
        .eq("session_type", "versus")
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Helper to check if user is a participant
      const isUserParticipant = (session: any) => {
        if (session.created_by === targetUserId) return true;
        const gameState = session.game_state as Record<string, unknown> | null;
        if (gameState?.challenged_user_id === targetUserId) return true;
        const participants = session.participants as Array<{ user_id: string }> | null;
        if (participants?.some((p: { user_id: string }) => p.user_id === targetUserId)) return true;
        return false;
      };

      const games: WaitingGame[] = (data || [])
        .filter(isUserParticipant)
        .map((session) => {
          const gameState = session.game_state as Record<string, unknown>;
          const isChallenger = session.created_by === targetUserId;
          
          return {
            id: session.id,
            game_slug: session.game_slug,
            other_player_name: isChallenger
              ? (gameState?.challenged_user_name as string) || "Tegenstander"
              : (gameState?.challenger_name as string) || "Iemand",
            created_at: session.created_at,
            mode: session.mode as "free" | "weekend",
            isSentByMe: isChallenger,
          };
        });

      setActiveGames(games);
    } catch (err) {
      console.error("Error fetching active games:", err);
    }
  }, [targetUserId]);

  // Fetch waiting and active games on mount and subscribe to updates
  useEffect(() => {
    fetchWaitingGames();
    fetchActiveGames();

    if (!targetUserId) return;

    const waitingChannel = supabase
      .channel(`home_waiting_games_${targetUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_sessions",
          filter: `status=eq.waiting`,
        },
        () => {
          fetchWaitingGames();
        }
      )
      .subscribe();

    const activeChannel = supabase
      .channel(`home_active_games_${targetUserId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "game_sessions",
          filter: `status=eq.active`,
        },
        () => {
          fetchActiveGames();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(waitingChannel);
      supabase.removeChannel(activeChannel);
    };
  }, [targetUserId, fetchWaitingGames, fetchActiveGames]);

  // Get upcoming events (next 3 events that haven't ended)
  const upcomingEvents = events
    .filter((e) => {
      const endDate = e.ends_at ? new Date(e.ends_at) : new Date(e.starts_at);
      return endDate >= new Date();
    })
    .slice(0, 3);

  /**
   * Refresh data when screen comes into focus
   * This ensures badge counts and game states are always up-to-date
   * after viewing notifications or returning from games
   */
  useFocusEffect(
    useCallback(() => {
      refetchNotifications();
      fetchWaitingGames();
      fetchActiveGames();
      refreshScores();
    }, [refetchNotifications, fetchWaitingGames, fetchActiveGames, refreshScores])
  );

  /**
   * Handle pull-to-refresh
   */
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([refreshProfile(), refreshScores(), refetchNotifications(), refetchEvents(), refreshActiveSpeurtocht(), fetchWaitingGames(), fetchActiveGames()]);
    setRefreshing(false);
  }, [refreshProfile, refreshScores, refetchNotifications, refetchEvents, refreshActiveSpeurtocht, fetchWaitingGames, fetchActiveGames]);

  /**
   * Format event date for display
   */
  const formatEventDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return `Vandaag ${format(date, "HH:mm", { locale: nl })}`;
    }
    if (isTomorrow(date)) {
      return `Morgen ${format(date, "HH:mm", { locale: nl })}`;
    }
    if (isThisWeek(date)) {
      return format(date, "EEEE HH:mm", { locale: nl });
    }
    return format(date, "d MMM HH:mm", { locale: nl });
  };

  // Get user's team position and next goal
  const teamPosition = getUserTeamPosition(userTeam?.id);
  const nextGoal = getNextGoal(userTeam?.id);

  // Calculate progress to next goal
  const currentScore = userTeam?.total_score || 0;
  const progressPercent = nextGoal ? Math.min((currentScore / nextGoal) * 100, 100) : 0;

  // Use activeProfile for display (could be self or a dependent)
  const currentProfile = activeProfile || profile;
  const displayName = currentProfile?.nickname || currentProfile?.full_name?.split(" ")[0] || "Gebruiker";

  const initials = currentProfile?.full_name
    ?.split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  return (
    <SafeAreaView className="flex-1" edges={["top", "left", "right"]} style={{ backgroundColor: colors.background }}>
      {/* Playing As Banner - shown when switched to a dependent */}
      <PlayingAsBanner />

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Header */}
        <View className="flex-row items-center justify-between px-4 py-3">
          {/* Left side - User Switcher or Profile */}
          {dependents.length > 0 || isSwitched ? (
            <UserSwitcher containerStyle={{ flex: 1 }} />
          ) : (
            <TouchableOpacity
              className="flex-row items-center flex-1"
              activeOpacity={0.7}
              onPress={() => router.push("/(tabs)/profile")}
              accessibilityRole="button"
              accessibilityLabel="Ga naar profiel"
            >
              <View
                className="w-10 h-10 rounded-full items-center justify-center mr-3 overflow-hidden"
                style={{ backgroundColor: isSwitched ? "#DBEAFE" : colors.primaryLight }}
              >
                {currentProfile?.avatar_url ? (
                  <Image
                    source={{ uri: currentProfile.avatar_url }}
                    className="w-full h-full"
                    resizeMode="cover"
                  />
                ) : (
                  <Text className="text-lg font-bold" style={{ color: isSwitched ? "#1D4ED8" : colors.primary }}>
                    {initials}
                  </Text>
                )}
              </View>
              <View className="flex-1">
                <Text className="text-lg font-bold" style={{ color: colors.text }}>
                  {userTeam?.name || "Geen team"}
                </Text>
                <Text style={{ color: colors.textSecondary }}>
                  Welkom terug, {displayName}!
                </Text>
              </View>
            </TouchableOpacity>
          )}

          {/* Notification Bell */}
          <TouchableOpacity 
            className="p-2 rounded-full relative"
            style={{ backgroundColor: colors.surfaceSecondary }}
            activeOpacity={0.7}
            onPress={() => router.push("/(tabs)/notifications")}
          >
            <Ionicons name="notifications-outline" size={24} color={colors.text} />
            {unreadCount > 0 && (
              <View
                className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full items-center justify-center px-1"
                style={{ backgroundColor: colors.error }}
              >
                <Text className="text-white text-xs font-bold">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>

        {/* Weekend Banner - shows active weekend or no weekend message */}
        <WeekendBanner />
        <NoWeekendBanner />

        {/* Active Games in Progress Banner */}
        {activeGames.length > 0 && (
          <TouchableOpacity
            className="mx-4 mt-4 rounded-2xl p-4 flex-row items-center"
            style={{ backgroundColor: "#22C55E" }}
            onPress={() => {
              // Navigate directly to the first active game
              const game = activeGames[0];
              const gamePath = game.game_slug === "puzzle_matching"
                ? `/(tabs)/games/memory/${game.id}`
                : game.game_slug === "tic_tac_toe"
                ? `/(tabs)/games/tic-tac-toe/${game.id}`
                : `/(tabs)/games/active-games`;
              router.push(gamePath as any);
            }}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={`${activeGames.length} actieve ${activeGames.length === 1 ? "spel" : "spellen"} voortzetten`}
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-4"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <Ionicons name="game-controller" size={24} color="#FFFFFF" />
            </View>
            <View className="flex-1">
              <Text className="font-bold text-white text-lg">
                🎮 {activeGames.length} actief {activeGames.length === 1 ? "spel" : "spellen"}
              </Text>
              <Text className="text-white/90">
                {activeGames.length === 1
                  ? `Speel verder tegen ${activeGames[0].other_player_name}`
                  : "Spellen in uitvoering"}
              </Text>
            </View>
            <View
              className="px-3 py-2 rounded-lg"
              style={{ backgroundColor: "rgba(255,255,255,0.3)" }}
            >
              <Text className="text-white font-semibold">Speel</Text>
            </View>
          </TouchableOpacity>
        )}

        {/* Waiting Games Card - Consolidated view */}
        {waitingGames.length > 0 && (
          <TouchableOpacity
            className="mx-4 mt-4 rounded-2xl p-4 flex-row items-center"
            style={{ 
              backgroundColor: waitingGames.some(g => !g.isSentByMe) ? "#F59E0B" : "#8B5CF6"
            }}
            onPress={() => router.push("/(tabs)/games/active-games" as any)}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel={`${waitingGames.length} actieve spellen bekijken`}
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mr-4"
              style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
            >
              <Ionicons 
                name={waitingGames.some(g => !g.isSentByMe) ? "game-controller" : "hourglass-outline"} 
                size={24} 
                color="#FFFFFF" 
              />
            </View>
            <View className="flex-1">
              <Text className="font-bold text-white text-lg">
                {waitingGames.some(g => !g.isSentByMe) 
                  ? `🎮 ${waitingGames.filter(g => !g.isSentByMe).length} ${waitingGames.filter(g => !g.isSentByMe).length === 1 ? "uitnodiging" : "uitnodigingen"}`
                  : `⏳ ${waitingGames.length} wachtend${waitingGames.length === 1 ? "" : "e"}`
                }
              </Text>
              <Text className="text-white/90">
                {waitingGames.some(g => !g.isSentByMe)
                  ? "Je hebt uitdagingen ontvangen!"
                  : "Wachten op reactie"}
              </Text>
            </View>
            <View
              className="px-3 py-2 rounded-lg"
              style={{ backgroundColor: "rgba(255,255,255,0.3)" }}
            >
              <Text className="text-white font-semibold">Bekijk</Text>
            </View>
          </TouchableOpacity>
        )}

        {/* Team Score Card - Only show if user is part of a team */}
        {userTeam && (
          <TouchableOpacity
            className="mx-4 mt-4 rounded-2xl p-6"
            style={{ backgroundColor: colors.primary }}
            activeOpacity={0.8}
            onPress={() => router.push(`/scores/team/${userTeam.id}`)}
            accessibilityRole="button"
            accessibilityLabel="Bekijk team score details"
          >
            <View className="flex-row justify-between items-center">
              <View className="flex-1">
                <Text className="text-white text-lg font-semibold mb-1">
                  Jouw Team Score
                </Text>
                <Text className="text-white/80 text-sm">
                  {userTeam.name} • Positie #{teamPosition || "–"}
                </Text>
              </View>
              <View 
                className="px-6 py-4 rounded-xl items-center justify-center"
                style={{ 
                  backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.25)" 
                }}
              >
                <Text className="text-white text-3xl font-bold">
                  {currentScore.toLocaleString()}
                </Text>
                <Text className="text-white/80 text-xs mt-1">
                  punten
                </Text>
              </View>
            </View>
          </TouchableOpacity>
        )}

        {/* Active Speurtocht Card - Only show when there's an active session */}
        {activeSession && (
          <View className="mx-4 mt-4">
            <ActiveSpeurttochtCard session={activeSession} />
          </View>
        )}

        {/* Active Quiz Card - Shows when there's a live quiz session */}
        <ActiveQuizCard />

        {/* Spellen & Uitdagingen Cards */}
        <View className="flex-row px-4 mt-6">
          {/* Spellen Card */}
          <TouchableOpacity
            onPress={() => router.push("/(tabs)/games")}
            className="flex-1 rounded-2xl p-4 mr-2"
            style={{
              backgroundColor: "#8B5CF615",
              borderWidth: 2,
              borderColor: "#8B5CF6",
            }}
            activeOpacity={0.7}
          >
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mb-3"
              style={{ backgroundColor: "#8B5CF625" }}
            >
              <Ionicons name="game-controller" size={24} color="#8B5CF6" />
            </View>
            <Text className="font-bold text-base" style={{ color: colors.text }}>
              Spellen
            </Text>
            <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
              Quiz, speurtocht & meer
            </Text>
            <View className="flex-row items-center mt-3">
              <Ionicons name="trophy" size={14} color="#F59E0B" />
              <Text className="text-xs ml-1 font-medium" style={{ color: "#F59E0B" }}>
                Verdien punten voor je team
              </Text>
            </View>
          </TouchableOpacity>

          {/* Uitdagingen Card */}
          <TouchableOpacity
            onPress={() => router.push("/(tabs)/games")}
            className="flex-1 rounded-2xl p-4 ml-2 relative"
            style={{
              backgroundColor: "#EA580C15",
              borderWidth: 2,
              borderColor: "#EA580C",
            }}
            activeOpacity={0.7}
          >
            {badgeCount > 0 && (
              <View
                className="absolute top-3 right-3 min-w-[22px] h-[22px] rounded-full items-center justify-center px-1.5"
                style={{ backgroundColor: "#EA580C" }}
              >
                <Text className="text-xs font-bold text-white">{badgeCount}</Text>
              </View>
            )}
            <View
              className="w-12 h-12 rounded-xl items-center justify-center mb-3"
              style={{ backgroundColor: "#EA580C25" }}
            >
              <Ionicons name="flash" size={24} color="#EA580C" />
            </View>
            <Text className="font-bold text-base" style={{ color: colors.text }}>
              Uitdagingen
            </Text>
            <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
              {activeChallenges.length} {activeChallenges.length === 1 ? "uitdaging" : "uitdagingen"} actief
            </Text>
            <View className="flex-row items-center mt-3">
              <Ionicons name="star" size={14} color="#F59E0B" />
              <Text className="text-xs ml-1 font-medium" style={{ color: "#F59E0B" }}>
                Foto opdrachten
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* Active Challenges Section - Only show when there are active challenges */}
        {activeChallenges.length > 0 && (
          <View
            className="mx-4 mt-4 rounded-2xl p-4"
            style={{
              backgroundColor: "#EA580C10",
              borderWidth: 1,
              borderColor: "#EA580C30",
            }}
          >
            <View className="flex-row items-center justify-between mb-3">
              <View className="flex-row items-center">
                <Ionicons name="flash" size={18} color="#EA580C" />
                <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                  Actieve Uitdagingen
                </Text>
              </View>
              {activeChallenges.length > 2 && (
                <TouchableOpacity onPress={() => router.push("/(tabs)/games")}>
                  <Text className="text-sm font-medium" style={{ color: "#EA580C" }}>
                    Bekijk alle
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            {activeChallenges.slice(0, 2).map((challenge) => (
              <TouchableOpacity
                key={challenge.id}
                onPress={() => {
                  // Route opdrachten to the opdracht screen
                  if (challenge.challenge_type === "opdracht") {
                    router.push(`/(tabs)/games/opdracht/${challenge.id}` as any);
                  } else {
                    router.push(`/(tabs)/games/challenge/${challenge.id}` as any);
                  }
                }}
                activeOpacity={0.7}
                className="rounded-xl p-3 mb-2 flex-row items-center"
                style={{
                  backgroundColor: colors.surface,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
              >
                <View
                  className="w-10 h-10 rounded-lg items-center justify-center"
                  style={{
                    backgroundColor: `${CHALLENGE_COLORS[challenge.challenge_type] || "#EA580C"}20`,
                  }}
                >
                  <Ionicons
                    name={(CHALLENGE_ICONS[challenge.challenge_type] || "flash") as any}
                    size={18}
                    color={CHALLENGE_COLORS[challenge.challenge_type] || "#EA580C"}
                  />
                </View>
                <View className="flex-1 ml-3">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    {challenge.title}
                  </Text>
                  <View className="flex-row items-center mt-1">
                    <Ionicons name="star" size={12} color="#F59E0B" />
                    <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
                      {challenge.points} punten
                    </Text>
                  </View>
                </View>
                <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Photos Card */}
        <TouchableOpacity
          onPress={() => router.push("/photos")}
          className="mx-4 mt-4 rounded-2xl p-4 flex-row items-center"
          style={{
            backgroundColor: "#EC489915",
            borderWidth: 2,
            borderColor: "#EC4899",
          }}
          activeOpacity={0.7}
        >
          <View className="w-14 h-14 mr-3">
            <LottieView
              source={isDark 
                ? require("@/assets/lottie/Photo_darkmode.json")
                : require("@/assets/lottie/Photo.json")
              }
              autoPlay
              loop
              style={{ width: 56, height: 56 }}
            />
          </View>
          <View className="flex-1">
            <Text className="font-bold text-base" style={{ color: colors.text }}>
              Foto's
            </Text>
            <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
              Bekijk en deel foto's van de familie
            </Text>
          </View>
          <Ionicons name="chevron-forward" size={20} color="#EC4899" />
        </TouchableOpacity>

        {/* Agenda Card */}
        <TouchableOpacity
          onPress={() => router.push("/agenda")}
          className="mx-4 mt-4 rounded-2xl p-4"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
          activeOpacity={0.7}
        >
          <View className="flex-row items-center justify-between mb-3">
            <View className="flex-row items-center">
              <View
                className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                style={{ backgroundColor: colors.primaryLight }}
              >
                <Ionicons name="calendar" size={20} color={colors.primary} />
              </View>
              <View>
                <Text className="font-bold" style={{ color: colors.text }}>
                  Agenda
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  Bekijk alle evenementen
                </Text>
              </View>
            </View>
            <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
          </View>
        </TouchableOpacity>

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <View className="mx-4 mt-4">
            <View className="flex-row items-center justify-between mb-3">
              <Text className="font-bold text-lg" style={{ color: colors.text }}>
                Aankomende Events
              </Text>
              <TouchableOpacity onPress={() => router.push("/agenda")}>
                <Text className="text-sm" style={{ color: colors.primary }}>
                  Alles bekijken
                </Text>
              </TouchableOpacity>
            </View>

            {upcomingEvents.map((event, index) => (
              <TouchableOpacity
                key={event.id}
                onPress={() => router.push(`/agenda/${event.id}`)}
                className={`rounded-xl p-3 flex-row items-center ${index > 0 ? "mt-2" : ""}`}
                style={{ backgroundColor: colors.surface }}
                activeOpacity={0.7}
              >
                <View
                  className="w-10 h-10 rounded-xl items-center justify-center mr-3"
                  style={{ backgroundColor: `${event.color || colors.primary}20` }}
                >
                  <Ionicons
                    name={(event.icon || "calendar") as any}
                    size={18}
                    color={event.color || colors.primary}
                  />
                </View>
                <View className="flex-1">
                  <Text
                    className="font-medium"
                    style={{ color: colors.text }}
                    numberOfLines={1}
                  >
                    {event.is_revealed ? event.title : event.secret_title || "???"}
                  </Text>
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    {formatEventDate(event.starts_at)}
                  </Text>
                </View>
                {isToday(new Date(event.starts_at)) && (
                  <View
                    className="px-2 py-1 rounded-full"
                    style={{ backgroundColor: colors.primaryLight }}
                  >
                    <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                      Vandaag
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </View>
        )}

        {/* Admin Panel (conditional) - Only for familie admins with active weekend */}
        {roles.isFamilieAdmin && activeWeekend && (
          <Link href="/(tabs)/admin" asChild>
            <TouchableOpacity
              className="mx-4 mt-4 rounded-xl p-4 flex-row items-center justify-between"
              style={{
                backgroundColor: isDark ? "#422006" : "#FFFBEB",
                borderWidth: 1,
                borderColor: isDark ? "#78350F" : "#FDE68A",
              }}
              activeOpacity={0.7}
            >
              <View className="flex-row items-center">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: isDark ? "#78350F" : "#FEF3C7" }}
                >
                  <Ionicons name="settings-outline" size={20} color="#D97706" />
                </View>
                <View>
                  <Text
                    className="font-semibold"
                    style={{ color: isDark ? "#FCD34D" : "#92400E" }}
                  >
                    Admin Panel
                  </Text>
                  <Text
                    className="text-sm"
                    style={{ color: isDark ? "#FBBF24" : "#B45309" }}
                  >
                    Beheer teams, scores & meer
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={20} color="#D97706" />
            </TouchableOpacity>
          </Link>
        )}

        {/* Team Rankings */}
        <TouchableOpacity
          className="mx-4 mt-6 rounded-2xl p-4"
          style={{ backgroundColor: colors.surfaceSecondary }}
          activeOpacity={0.8}
          onPress={() => router.push("/scores")}
          accessibilityRole="button"
          accessibilityLabel="Bekijk volledig scorebord"
        >
          <View className="flex-row items-center justify-between mb-4">
            <Text className="text-lg font-bold" style={{ color: colors.text }}>
              Team Rankings
            </Text>
            <View className="flex-row items-center">
              <Text className="text-sm" style={{ color: colors.primary }}>
                Bekijk alles
              </Text>
              <Ionicons 
                name="chevron-forward" 
                size={16} 
                color={colors.primary} 
                style={{ marginLeft: 4 }}
              />
            </View>
          </View>

          {teams.length === 0 ? (
            <View className="py-4 items-center">
              <Text style={{ color: colors.textSecondary }}>
                Nog geen teams
              </Text>
            </View>
          ) : (
            teams.slice(0, 3).map((team, index) => {
              const rankColors = ["#7C3AED", "#6B7280", "#D97706"];
              return (
                <View
                  key={team.id}
                  className={`rounded-xl p-3 flex-row items-center justify-between ${index > 0 ? "mt-2" : ""}`}
                  style={{ backgroundColor: colors.surface }}
                >
                  <View className="flex-row items-center flex-1">
                    <Text 
                      className="text-lg font-bold mr-3 w-6" 
                      style={{ color: rankColors[index] || colors.textTertiary }}
                    >
                      {team.rank}
                    </Text>
                    {team.color && (
                      <View 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: team.color }}
                      />
                    )}
                    <Text 
                      className="font-medium flex-1" 
                      style={{ color: colors.text }}
                      numberOfLines={1}
                    >
                      {team.name}
                    </Text>
                  </View>
                  <Text className="font-semibold" style={{ color: colors.textSecondary }}>
                    {team.total_score.toLocaleString()}
                  </Text>
                </View>
              );
            })
          )}
        </TouchableOpacity>

        {/* Active Weekend Info */}
        {activeWeekend && (
          <View
            className="mx-4 mt-6 rounded-2xl p-4"
            style={{
              backgroundColor: colors.primaryLight,
              borderWidth: 1,
              borderColor: isDark ? "#6D28D9" : "#DDD6FE",
            }}
          >
            <View className="flex-row items-center mb-2">
              <Ionicons name="calendar" size={20} color={colors.primary} />
              <Text
                className="font-semibold ml-2"
                style={{ color: isDark ? "#C4B5FD" : "#5B21B6" }}
              >
                {activeWeekend.name}
              </Text>
            </View>
            {activeWeekend.location_name && (activeWeekend.weekend_location_revealed || roles.isFamilieAdmin) && (
              <Text style={{ color: isDark ? "#A78BFA" : "#7C3AED" }} className="text-sm">
                📍 {activeWeekend.location_name}
                {!activeWeekend.weekend_location_revealed && roles.isFamilieAdmin && " (verborgen)"}
              </Text>
            )}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
